import { Component, OnInit, signal, inject, input, output, DestroyRef } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { ButtonLoadingIndicatorComponent } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { EmailLabelingConfigService } from './email-labeling-config.service';
import { MessageCategoryConfig } from './models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, forkJoin } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';

@Component({
  selector: 'email-labeling-config-assignment',
  standalone: true,
  imports: [
    TranslateModule,
    MatSelectModule,
    MatButtonModule,
    GalaxyFormFieldModule,
    GalaxyAlertModule,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
    ButtonLoadingIndicatorComponent,
  ],
  templateUrl: './email-labeling-config-assignment.component.html',
  styleUrls: ['./email-labeling-config-assignment.component.scss'],
})
export class EmailLabelingConfigAssignmentComponent implements OnInit {
  readonly namespace = input.required<string>();
  readonly userId = input.required<string>();
  readonly readonly = input<boolean>(false);
  readonly viewDetailsUrlBase = input<string>('/email-labeling-settings');

  readonly configurationApplied = output<string | null>();
  readonly cancelled = output<void>();
  readonly navigateToConfig = output<void>();

  readonly availableConfigs = signal<MessageCategoryConfig[]>([]);
  readonly selectedConfigId = signal<string | null>(null);
  readonly currentConfigId = signal<string | null>(null);
  readonly loading = signal<boolean>(true);
  readonly saving = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  private readonly service = inject(EmailLabelingConfigService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loading.set(true);
    this.error.set(null);

    const namespace = this.namespace();
    const userId = this.userId();

    if (!namespace || !userId) {
      this.error.set(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.LOAD_ERROR'));
      this.loading.set(false);
      return;
    }

    forkJoin({
      configs: this.service.listConfigs(namespace),
      appliedConfigId: this.service.getAppliedConfig(namespace, userId),
    })
      .pipe(
        tap(({ configs, appliedConfigId }) => {
          this.availableConfigs.set(configs);
          this.currentConfigId.set(appliedConfigId);
          this.selectedConfigId.set(appliedConfigId);
        }),
        catchError((_err) => {
          this.error.set(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.LOAD_ERROR'));
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onConfigChange(configId: string): void {
    this.selectedConfigId.set(configId);
  }

  onSave(): void {
    const namespace = this.namespace();
    const userId = this.userId();

    if (!namespace || !userId) {
      return;
    }

    const configId = this.selectedConfigId();
    const save$ = configId
      ? this.service.applyConfigToUser(namespace, userId, configId)
      : this.service.clearAppliedConfig(namespace, userId);

    this.saving.set(true);
    this.error.set(null);

    save$
      .pipe(
        tap(() => {
          this.currentConfigId.set(configId);
          this.snackbarService.openSuccessSnack('ADMINISTRATION.EMAIL_LABELING_SETTINGS.ASSIGNMENT_SUCCESS');
          this.configurationApplied.emit(configId);
        }),
        catchError((_err) => {
          this.snackbarService.openErrorSnack('ADMINISTRATION.EMAIL_LABELING_SETTINGS.ASSIGNMENT_ERROR');
          return EMPTY;
        }),
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onGoToConfig(): void {
    this.navigateToConfig.emit();
  }

  hasChanges(): boolean {
    return this.selectedConfigId() !== this.currentConfigId();
  }
}
